/**
 * InvoiceDetailSheet — Sheet latéral unique pour visualiser une facture.
 *
 * Utilisé par :
 *   - /app/invoices (InvoicesList.jsx)
 *   - /app/clients (Clients.jsx, historique)
 *   - Tout endroit qui a besoin d'afficher rapidement une facture.
 *
 * Deep-link vers /app/invoices/:id (page complète) pour les actions avancées :
 * émission, avoir, paiement, relances.
 *
 * Props :
 *   - invoiceId : string | null. Si fourni, le sheet fetch les données.
 *   - invoice   : objet facture direct (utile quand on l'a déjà en mémoire).
 *   - open      : bool
 *   - onOpenChange : (bool) => void
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
SheetDescription, } from "@/components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FileText, Paperclip, Eye, ExternalLink, Download, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import { invoiceStatusFr } from "@/lib/statusLabels";

const fmtCurrency = (v) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(v) || 0);

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return "—"; }
};

const STATUS_STYLE = {
  paid: "bg-green-50 text-green-700 border-green-200",
  issued: "bg-blue-50 text-blue-700 border-blue-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function InvoiceDetailSheet({ invoiceId, invoice, open, onOpenChange, onDeleted }) {
  const [full, setFull] = useState(invoice || null);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // Refetch every time invoiceId changes AND the sheet is open — this
  // guarantees fresh totals (payment, credit note) each open.
  useEffect(() => {
    if (!open) return;
    if (!invoiceId) { setFull(invoice || null); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/invoices/${invoiceId}`);
        setFull(data.invoice);
      } catch (e) {
        toast.error(apiError(e));
        onOpenChange?.(false);
      } finally { setLoading(false); }
    })();
  }, [invoiceId, open]); // eslint-disable-line

  const inv = full;
  const num = inv?.number || inv?.invoice_number;
  const totals = inv?.totals || {};
  const total_ht  = totals.subtotal_ht ?? inv?.total_ht ?? 0;
  const total_vat = totals.total_vat  ?? inv?.total_vat ?? 0;
  const total_ttc = totals.total_ttc  ?? inv?.total_ttc ?? 0;
  const source = inv?.imported_from?.split(":")[0] || "native";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-white p-0 flex flex-col"
                     data-testid="invoice-detail-sheet">
        <SheetHeader className="px-6 py-4 border-b border-slate-200 shrink-0">
          <SheetTitle className="text-slate-900 flex items-center gap-2 font-mono text-base pr-8">
          <FileText className="w-5 h-5 text-blue-600 shrink-0" />
          <span className="truncate">{num || "Brouillon"}</span>
          {inv?.file_asset_id && <Paperclip className="w-4 h-4 text-blue-500 shrink-0" />}
          {inv?.status && (
            <Badge variant="outline" className={cn("text-[10px] uppercase ml-auto mr-6",
              STATUS_STYLE[inv.status] || STATUS_STYLE.draft)}>
              {invoiceStatusFr(inv.status)}
            </Badge>
          )}
        </SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-5 space-y-4">
            {loading ? (
              <div className="skeleton h-32 rounded-lg" />
            ) : !inv ? (
              <div className="text-sm text-slate-500 py-10 text-center">Aucune facture sélectionnée.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Client</p>
                    <p className="text-sm font-medium text-slate-900 truncate">{inv.client?.name || "—"}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Source</p>
                    <Badge variant="outline" className={cn("text-[10px] uppercase",
                      source === "axonaut" ? "bg-orange-50 text-orange-700 border-orange-200" :
                      source === "pennylane" ? "bg-violet-50 text-violet-700 border-violet-200" :
                      source === "iopole" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                      "bg-blue-50 text-blue-700 border-blue-200")}>
                      {source}
                    </Badge>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Émise le</p>
                    <p className="text-sm font-mono text-slate-900">{fmtDate(inv.issued_at || inv.invoice_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Échéance</p>
                    <p className="text-sm font-mono text-slate-900">{fmtDate(inv.due_date)}</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total HT</p>
                      <p className="text-sm font-mono font-medium text-slate-900"
                          data-testid="sheet-total-ht">{fmtCurrency(total_ht)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">TVA</p>
                      <p className="text-sm font-mono font-medium text-slate-600">{fmtCurrency(total_vat)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total TTC</p>
                      <p className="text-base font-mono font-bold text-blue-600"
                          data-testid="sheet-total-ttc"
                          style={{ fontFamily: "Manrope, sans-serif" }}>{fmtCurrency(total_ttc)}</p>
                    </div>
                  </div>
                </div>

                {inv.file_asset_id && (
                  <a href={`${API}/files/${inv.file_asset_id}`} target="_blank" rel="noreferrer">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
                            data-testid="sheet-view-pdf-btn">
                      <Eye className="w-4 h-4 mr-2" /> Voir la facture PDF
                    </Button>
                  </a>
                )}

                {inv.id && (
                  <Link to={`/app/invoices/${inv.id}`}>
                    <Button variant="outline" className="w-full border-slate-200 h-10"
                            data-testid="sheet-open-full-btn">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ouvrir la fiche complète (paiements, avoirs, relances)
                    </Button>
                  </Link>
                )}

                {/* Draft actions — édition + suppression + aperçu PDF depuis le sheet */}
                {inv.status === "draft" && (
                  <>
                    <Button variant="outline"
                             className="w-full border-violet-200 h-10 text-violet-700 hover:bg-violet-50"
                             onClick={async () => {
                               try {
                                 const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
                                 const url = URL.createObjectURL(res.data);
                                 window.open(url, "_blank");
                               } catch (err) { toast.error(apiError(err)); }
                             }}
                             data-testid="sheet-preview-pdf-btn">
                      <Eye className="w-4 h-4 mr-1.5" />
                      Aperçu PDF (brouillon)
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to={`/app/invoices/${inv.id}/edit`}>
                        <Button variant="outline" className="w-full border-blue-200 h-10 text-blue-700 hover:bg-blue-50"
                                data-testid="sheet-edit-draft-btn">
                          Modifier le brouillon
                        </Button>
                      </Link>
                      <Button variant="outline"
                               className="w-full border-red-200 h-10 text-red-600 hover:bg-red-50"
                               onClick={() => setConfirmDelete(true)}
                               disabled={deleting}
                               data-testid="sheet-delete-draft-btn">
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Supprimer le brouillon
                      </Button>
                    </div>
                  </>
                )}

                {inv.lines?.length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                      Lignes ({inv.lines.length})
                    </h4>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs uppercase text-slate-500">Désignation</TableHead>
                            <TableHead className="text-xs uppercase text-slate-500 text-center w-14">Qté</TableHead>
                            <TableHead className="text-xs uppercase text-slate-500 text-right w-24">P.U. HT</TableHead>
                            <TableHead className="text-xs uppercase text-slate-500 text-right w-24">HT</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inv.lines.map((l, i) => {
                            const net = Number(l.quantity || 0) * Number(l.unit_price_ht || 0) *
                                        (1 - Number(l.discount_percent || l.discount_pct || 0) / 100);
                            return (
                              <TableRow key={l.id || i}>
                                <TableCell className="text-sm max-w-[220px]">
                                  <span className="line-clamp-2">{l.description}</span>
                                </TableCell>
                                <TableCell className="font-mono text-sm text-center tabular-nums">{l.quantity}</TableCell>
                                <TableCell className="font-mono text-sm text-right tabular-nums">
                                  {fmtCurrency(l.unit_price_ht)}
                                </TableCell>
                                <TableCell className="font-mono text-sm text-right tabular-nums">
                                  {fmtCurrency(l.total_ht ?? net)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
      {/* Confirm delete draft */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="sheet-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce brouillon ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le brouillon et toutes ses lignes seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="sheet-confirm-delete-btn"
              onClick={async (e) => {
                e.preventDefault();
                if (!inv?.id) return;
                setDeleting(true);
                try {
                  await api.delete(`/invoices/${inv.id}`);
                  toast.success("Brouillon supprimé");
                  setConfirmDelete(false);
                  onOpenChange?.(false);
                  onDeleted?.(inv.id);
                } catch (err) { toast.error(apiError(err)); }
                finally { setDeleting(false); }
              }}>
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
